import { Article, ArticleListRouteKey, OeArticlesWithArticles, PureArticleListType, WholesalerArticle } from "@tm/models"
import { useQuery } from "react-query"
import { usePureArticleList, useWorkTaskId } from "@tm/context-distribution"
import { useMemo } from "react"
import { crossSellingListId } from "@bundles/parts/components/ListV2/components/CrossSelling/crossSellingStates"
import { loadArticles } from "../../data"
import { useVehicle } from "../../../ListV2/hooks"
import { PartsListImage, PartsListImageCoordinate } from "../../../../data/model"
import { ArticleGroupParams, PureListArticleData, PureListArticlesResponse, WholesalerArticleGroup } from "../../../ListV2/models"
import { emptyFunc, getArticleGroups, mapDistinctValues } from "../../../ListV2/helpers"
import { useArticleListFeaturesDisable } from "../../../ListV2/ArticleListConfiguration/ArticleListFeaturesDisable"
import { ParticularState, useParticularContext } from "../Particular/ParticularContext"

async function loadArticlesFromContext(particularContext: ParticularState): Promise<PureListArticlesResponse> {
    switch (particularContext.type) {
        case "damageModulesList":
            const { articles: articlesFromDat } = particularContext
            return {
                type: particularContext.type,
                articles: articlesFromDat?.map((x) => x.article).filter((article): article is Article => article !== null) ?? [],
                oeArticles: (articlesFromDat as OeArticlesWithArticles[]) ?? [],
            }

        case "wheelsOverview":
            const { articles: articlesFromOverview } = particularContext
            return {
                type: particularContext.type,
                articles: articlesFromOverview ?? [],
            }

        case crossSellingListId:
            const { articles: recommendedArticles } = particularContext
            return {
                type: particularContext.type,
                articles: recommendedArticles ?? [],
            }

        default:
            return { type: null, articles: [] }
    }
}

export function useArticles(listId: ArticleListRouteKey, pureListType: PureArticleListType): PureListArticleData {
    const workTaskId = useWorkTaskId()
    const vehicle = useVehicle()
    const { state } = usePureArticleList(listId)
    const particularContext = useParticularContext()
    const disableArticleGrouping = useArticleListFeaturesDisable((x) => x.disableArticleGrouping)

    const requestKey = particularContext?.type === pureListType ? particularContext?.request : state?.request

    const articlesQuery = useQuery({
        queryKey: ["pureList", workTaskId, pureListType, requestKey],
        queryFn: () => {
            if (particularContext?.type === pureListType) {
                return loadArticlesFromContext(particularContext as ParticularState)
            }

            if (state.request) {
                return loadArticles(pureListType, state.request, vehicle)
            }

            return undefined
        },
        enabled: !!requestKey,
    })

    const isLoaded = articlesQuery.isSuccess || articlesQuery.isError
    const articles = useMemo(() => articlesQuery.data?.articles ?? [], [articlesQuery.data])
    const supplierIds = useMemo(() => mapDistinctValues(articles, (x) => x.supplier.id), [articles])
    const productGroupIds = useMemo(() => mapDistinctValues(articles, (x) => x.productGroup.id), [articles])

    const articleGroups = useMemo(() => {
        if (disableArticleGrouping) {
            return []
        }

        const groups: (ArticleGroupParams | WholesalerArticleGroup)[] = []
        if (articlesQuery.data?.articles) {
            groups.push(...getArticleGroups(articlesQuery.data.articles, false))
        }
        if ((articlesQuery.data?.type === "alternative" || articlesQuery.data?.type === "suggestion") && articlesQuery.data.wholesalerParts?.length) {
            groups.unshift({ type: "WholesalerArticle", articles: articlesQuery.data.wholesalerParts })
        }
        return groups
    }, [articlesQuery.data?.articles, articlesQuery.data?.type, disableArticleGrouping])

    return useMemo(
        () => ({
            requestStatus: articlesQuery.status,
            isEnabled: true,
            isLoading: articlesQuery.isFetching,
            isLoaded,
            isSuccess: articlesQuery.isSuccess,
            isFailed: articlesQuery.isError,
            articles,
            articleCount: articles.length,
            articleGroups,
            supplierIds,
            productGroupIds,
            pageCount: 0,
            hasNextPage: false,
            isFetchingNextPage: false,
            loadNextPage: emptyFunc,
            response: articlesQuery.data,
        }),
        [
            articleGroups,
            articles,
            articlesQuery.data,
            articlesQuery.isError,
            articlesQuery.isFetching,
            articlesQuery.isSuccess,
            articlesQuery.status,
            isLoaded,
            productGroupIds,
            supplierIds,
        ]
    )
}

export type LoadArticlesResponse = {
    articles: Article[]
} & (
    | { type: null }
    | {
          type: "alternative"
          wholesalerParts?: WholesalerArticle[]
      }
    | {
          type: "partslist"
          imageCoordinates?: PartsListImageCoordinate[]
          partsListImage?: PartsListImage
      }
    | {
          type: "accessoryList"
      }
    | {
          type: "articlesAlreadyOrderedList"
      }
    | {
          type: "mainAccessoryList"
      }
    | {
          type: "recommended-articles"
      }
    | {
          type: "damageModulesList"
      }
    | {
          type: "mainPartsList"
      }
)
